import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  IconButton,
  Avatar,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogContent,
  DialogActions,
  alpha,
  ImageList,
  ImageListItem,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Home as HomeIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  CalendarToday as CalendarIcon,
  AttachMoney as MoneyIcon,
  Business as BusinessIcon,
  Check as CheckIcon,
  CameraAlt as CameraIcon,
  Visibility as ViewIcon,
  Settings as SettingsIcon,
  Water,
  Wifi,
  Bolt,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router/dist';
import { usePropertyInventory } from '../../../hooks/usePropertyInventory';
import { ApartmentStatus } from '../../../types/propertyInventory/ApartmentComplex';
import { getApartmentTypeText } from '../../../utils/constants';
import { ApartmentService } from '../../../types/propertyInventory/ApartmentService';
import { CreateRentPaymentDialog } from './dialogs/CreateRentPaymentDialog';
import { EditApartmentDialog } from './dialogs/EditApartmentDialog';
import { DeleteApartmentDialog } from './dialogs/DeleteApartmentDialog';
import { PaymentHistory } from './dialogs/PaymentHistory';
import { TenantHistoryDialog } from './dialogs/TenantHistoryDialog';
import { DeleteCurrentTenant } from './dialogs/DeleteCurrentTenant';
import { AddTenantDialog } from './dialogs/AddTenantDialog';


const getStatusInfo = (status: ApartmentStatus) => {
  switch (status) {
    case ApartmentStatus.DISPONIBLE:
      return { label: 'Disponible', color: '#4CAF50', bg: alpha('#4CAF50', 0.1) };
    case ApartmentStatus.OCUPADO:
      return { label: 'Ocupado', color: '#FF9800', bg: alpha('#FF9800', 0.1) };
    case ApartmentStatus.NO_DISPONIBLE:
      return { label: 'No disponible', color: '#F44336', bg: alpha('#F44336', 0.1) };
    default:
      return { label: 'Desconocido', color: '#9E9E9E', bg: alpha('#9E9E9E', 0.1) };
  }
};

type ServiceItemProps = {
  icon: React.ReactNode;
  serviceData?: ApartmentService;
};

const ServiceItem: React.FC<ServiceItemProps> = ({ icon, serviceData }) => {
  return (
    <ListItem sx={{ px: 0 }}>
      <ListItemIcon>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 40,
          height: 40,
          borderRadius: '50%',
          backgroundColor: alpha('#4CAF50', 0.1)
        }}>
          <Typography sx={{ fontSize: '1.2rem' }}>{icon}</Typography>
        </Box>
      </ListItemIcon>
      <ListItemText
        primary={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body1" fontWeight="medium">
              {serviceData?.serviceType}
            </Typography>
            <CheckIcon sx={{ color: '#4CAF50', fontSize: 18 }} />
          </Box>
        }
        secondary={
          serviceData ? (
            <Box sx={{ mt: 0.5 }}>
              <br />
              <Typography variant="caption" color="text.secondary">
                Número de cuenta: {serviceData.serviceAccount || 'No especificado'}
              </Typography>
              <br />
              <Typography variant="caption" color="text.secondary">
                Proveedor: {serviceData.provider || 'No especificado'}
              </Typography>
              <br />
              <Typography variant="caption" color="text.secondary">
                Incluido en renta: {serviceData.isIncludedInRent ? 'Sí' : 'No'}
              </Typography>
            </Box>
          ) : (
            <Typography variant="caption" color="text.secondary">
              No incluido
            </Typography>
          )
        }
      />
    </ListItem>
  );
};

const ApartmentDetailsPage = () => {
  const { apartmentId } = useParams();
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [imageDialogOpen, setImageDialogOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState('');
  const [addPaymentDialogOpen, setAddPaymentDialogOpen] = useState(false);
  const [paymentHistoryDialogOpen, setPaymentHistoryDialogOpen] = useState(false);
  const [tenantsHistoryDialogOpen, setTenantsHistoryDialogOpen] = useState(false);
  const [removeTenantDialogOpen, setRemoveTenantDialogOpen] = useState(false);
  const [addTenantDialogOpen, setAddTenantDialogOpen] = useState(false);
  const { getApartmentById, apartmentById } = usePropertyInventory();
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      if (apartmentId) {
        await getApartmentById(apartmentId);
      }
    })();
  }, [apartmentId, getApartmentById]);

  const handleImageClick = (imageUrl: React.SetStateAction<string>) => {
    setSelectedImage(imageUrl);
    setImageDialogOpen(true);
  };

  const handleEditSave = () => {
    setEditDialogOpen(false);
    getApartmentById(apartmentId as string);
  };

  const handleDelete = () => {
    setDeleteDialogOpen(false);
    navigate('/properties-inventory/complex/' + apartmentById?.complex.id);
  };

  const handleAddPayment = () => {
    setAddPaymentDialogOpen(false);
    getApartmentById(apartmentId as string);
  };

  const handleRemoveTenant = () => {
    setRemoveTenantDialogOpen(false);
    getApartmentById(apartmentId as string);
  };

  const handleAddTenant = () => {
    setAddTenantDialogOpen(false);
    getApartmentById(apartmentId as string);
  };

  const statusInfo = getStatusInfo(apartmentById?.status as ApartmentStatus);

  return (
    <Box sx={{ backgroundColor: 'white', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{
        p: 3,
        borderBottom: '1px solid #E0E0E0',
        backgroundColor: 'white',
        mb: 3
      }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <IconButton
                sx={{
                  mr: 2,
                  backgroundColor: alpha('#4DB6AC', 0.1),
                  '&:hover': {
                    backgroundColor: alpha('#4DB6AC', 0.2),
                  }
                }}
                onClick={() => navigate(-1)}
              >
                <ArrowBackIcon sx={{ color: '#4DB6AC' }} />
              </IconButton>
              <HomeIcon sx={{ mr: 2, color: '#4DB6AC' }} />
              <Box>
                <Typography variant="h5" component="div" fontWeight="bold" color="#2E7D7A">
                  Apartamento {apartmentById?.apartmentNumber}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {apartmentById?.complex.name} • {apartmentById?.complex.location}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={() => setEditDialogOpen(true)}
                sx={{
                  borderColor: '#4DB6AC',
                  color: '#4DB6AC',
                  '&:hover': {
                    borderColor: '#26A69A',
                    backgroundColor: alpha('#4DB6AC', 0.1)
                  }
                }}
              >
                Editar
              </Button>
              <Button
                variant="outlined"
                startIcon={<DeleteIcon />}
                onClick={() => setDeleteDialogOpen(true)}
                sx={{
                  borderColor: '#F44336',
                  color: '#F44336',
                  '&:hover': {
                    borderColor: '#D32F2F',
                    backgroundColor: alpha('#F44336', 0.1)
                  }
                }}
              >
                Eliminar
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg">
        <Grid container spacing={4}>
          {/* Columna izquierda - Información principal */}
          <Grid item xs={12} md={8}>
            {/* Información básica */}
            <Card sx={{ mb: 4 }}>
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                  <Typography variant="h6" fontWeight="bold" color="#2E7D7A">
                    Información General
                  </Typography>
                  <Chip
                    label={statusInfo.label}
                    sx={{
                      backgroundColor: statusInfo.bg,
                      color: statusInfo.color,
                      fontWeight: 'bold'
                    }}
                  />
                </Box>

                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <HomeIcon sx={{ color: '#4DB6AC', mr: 2 }} />
                      <Box>
                        <Typography variant="body2" color="text.secondary">Tipo</Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {getApartmentTypeText(apartmentById?.apartmentType || 1)}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <MoneyIcon sx={{ color: '#4DB6AC', mr: 2 }} />
                      <Box>
                        <Typography variant="body2" color="text.secondary">Precio Mensual</Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {apartmentById?.currency === 'USD' ? '$' : '₡'}
                          {apartmentById?.baseRentAmount ? apartmentById?.baseRentAmount.toLocaleString() : 'No especificado'}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <CalendarIcon sx={{ color: '#4DB6AC', mr: 2 }} />
                      <Box>
                        <Typography variant="body2" color="text.secondary">Fecha de Pago</Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {apartmentById?.currentTenant?.createdAt
                            ? new Date(apartmentById.currentTenant.createdAt).toLocaleDateString('es-CR')
                            : 'No especificado'}
                        </Typography>
                      </Box>
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <BusinessIcon sx={{ color: '#4DB6AC', mr: 2 }} />
                      <Box>
                        <Typography variant="body2" color="text.secondary">Último Pago</Typography>
                        <Typography variant="body1" fontWeight="medium">
                          {apartmentById?.currentTenant?.rentPayments && apartmentById.currentTenant.rentPayments.length > 0
                            ? new Date(apartmentById.currentTenant.rentPayments[0].paymentDate).toLocaleDateString('es-CR')
                            : 'No hay pagos'}
                        </Typography>
                        {apartmentById?.currentTenant?.rentPayments && apartmentById.currentTenant.rentPayments.length > 0 && (
                          <Chip
                            label="Pagado"
                            size="small"
                            sx={{
                              backgroundColor: alpha('#4CAF50', 0.1),
                              color: '#388E3C',
                              fontSize: '0.7rem'
                            }}
                          />
                        )}
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Servicios */}
            <Card sx={{ mb: 4 }}>
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h6" fontWeight="bold" color="#2E7D7A" sx={{ mb: 3 }}>
                  Servicios Incluidos
                </Typography>

                <List>
                  {apartmentById?.services.length === 0 && (
                    <Typography variant="body2" color="text.secondary">
                      No hay servicios asociados a este apartamento.
                    </Typography>
                  )}
                  {apartmentById?.services.map((service) => (
                    <ServiceItem
                      key={service.id}
                      icon={service.serviceType === 'Electricidad' ? <Bolt /> : service.serviceType === 'Agua' ? <Water /> : <Wifi />}
                      serviceData={service}
                    />
                  ))}
                </List>
              </CardContent>
            </Card>

            {/* Galería de fotos */}
            <Card>
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                  <Typography variant="h6" fontWeight="bold" color="#2E7D7A">
                    Galería ({apartmentById?.multimedia.length} fotos)
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={<CameraIcon />}
                    size="small"
                    sx={{
                      borderColor: '#4DB6AC',
                      color: '#4DB6AC'
                    }}
                  >
                    Agregar Fotos
                  </Button>
                </Box>

                <ImageList cols={2} gap={16}>
                  {(apartmentById?.multimedia ?? []).map((photo, index) => (
                    <ImageListItem key={index}>
                      <Box
                        sx={{
                          position: 'relative',
                          cursor: 'pointer',
                          borderRadius: 2,
                          overflow: 'hidden',
                          '&:hover .overlay': {
                            opacity: 1
                          }
                        }}
                        onClick={() => handleImageClick(photo)}
                      >
                        <img
                          src={photo}
                          alt={`Apartamento ${apartmentId ?? ''} - ${index + 1}`}
                          style={{
                            width: '100%',
                            height: 200,
                            objectFit: 'cover'
                          }}
                        />
                        <Box
                          className="overlay"
                          sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: 'rgba(0,0,0,0.5)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            opacity: 0,
                            transition: 'opacity 0.3s ease'
                          }}
                        >
                          <ViewIcon sx={{ color: 'white', fontSize: 40 }} />
                        </Box>
                      </Box>
                    </ImageListItem>
                  ))}
                </ImageList>
              </CardContent>
            </Card>
          </Grid>

          {/* Columna derecha - Inquilino */}
          <Grid item xs={12} md={4}>
            {apartmentById?.status === ApartmentStatus.OCUPADO && apartmentById?.currentTenant ? (
              <Card sx={{ position: 'sticky', top: 20 }}>
                <CardContent sx={{ p: 4 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                    <Typography variant="h6" fontWeight="bold" color="#2E7D7A">
                      Inquilino Actual
                    </Typography>
                    <IconButton size="small">
                      <SettingsIcon sx={{ color: '#4DB6AC' }} />
                    </IconButton>
                  </Box>

                  <Box sx={{ textAlign: 'center', mb: 3 }}>
                    <Avatar
                      sx={{
                        width: 80,
                        height: 80,
                        backgroundColor: '#4DB6AC',
                        fontSize: '2rem',
                        mx: 'auto',
                        mb: 2
                      }}
                    >
                      {apartmentById?.currentTenant?.fullName.charAt(0)}
                    </Avatar>
                    <Typography variant="h6" fontWeight="bold">
                      {apartmentById?.currentTenant?.fullName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {apartmentById?.currentTenant?.profession}
                    </Typography>
                  </Box>

                  <Divider sx={{ mb: 3 }} />

                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <PersonIcon sx={{ color: '#4DB6AC', mr: 2, fontSize: 20 }} />
                      <Box>
                        <Typography variant="caption" color="text.secondary">Cédula</Typography>
                        <Typography variant="body2">{apartmentById?.currentTenant?.documentNumber}</Typography>
                      </Box>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <PhoneIcon sx={{ color: '#4DB6AC', mr: 2, fontSize: 20 }} />
                      <Box>
                        <Typography variant="caption" color="text.secondary">Teléfono</Typography>
                        <Typography variant="body2">{apartmentById?.currentTenant?.primaryPhone}</Typography>
                      </Box>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <EmailIcon sx={{ color: '#4DB6AC', mr: 2, fontSize: 20 }} />
                      <Box>
                        <Typography variant="caption" color="text.secondary">Email</Typography>
                        <Typography variant="body2">{apartmentById?.currentTenant?.email}</Typography>
                      </Box>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <CalendarIcon sx={{ color: '#4DB6AC', mr: 2, fontSize: 20 }} />
                      <Box>
                        <Typography variant="caption" color="text.secondary">Fecha de Ingreso</Typography>
                        <Typography variant="body2">
                          {apartmentById?.currentTenant?.createdAt && new Date(apartmentById?.currentTenant?.createdAt).toLocaleDateString('es-CR')}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                  {apartmentById?.currentTenant?.emergencyContactName && (
                    <>
                      <Divider sx={{ mb: 2 }} />
                      <Box>
                        <Typography variant="caption" color="text.secondary">Contacto de Emergencia</Typography>
                        <Typography variant="body2">{apartmentById?.currentTenant?.emergencyContactName}</Typography>
                        <Typography variant="body2">{apartmentById?.currentTenant?.emergencyContactPhone}</Typography>
                      </Box>
                    </>
                  )}

                  {apartmentById?.currentTenant?.notes && (
                    <>
                      <Divider sx={{ my: 2 }} />
                      <Box>
                        <Typography variant="caption" color="text.secondary">Notas</Typography>
                        <Typography variant="body2">{apartmentById?.currentTenant?.notes}</Typography>
                      </Box>
                    </>
                  )}

                  <Box sx={{ mt: 3 }}>
                    <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        fullWidth
                        startIcon={<CalendarIcon />}
                        onClick={() => setPaymentHistoryDialogOpen(true)}
                        sx={{
                          borderColor: '#4DB6AC',
                          color: '#4DB6AC',
                          '&:hover': {
                            borderColor: '#26A69A',
                            backgroundColor: alpha('#4DB6AC', 0.1)
                          }
                        }}
                      >
                        Ver Historial Pagos
                      </Button>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                      <Button
                        variant="contained"
                        size="small"
                        fullWidth
                        startIcon={<MoneyIcon />}
                        onClick={() => setAddPaymentDialogOpen(true)}
                        sx={{
                          backgroundColor: '#4DB6AC',
                          '&:hover': {
                            backgroundColor: '#26A69A'
                          }
                        }}
                      >
                        Agregar Pago
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        fullWidth
                        startIcon={<PersonIcon />}
                        onClick={() => setTenantsHistoryDialogOpen(true)}
                        sx={{
                          borderColor: '#4DB6AC',
                          color: '#4DB6AC',
                          '&:hover': {
                            borderColor: '#26A69A',
                            backgroundColor: alpha('#4DB6AC', 0.1)
                          }
                        }}
                      >
                        Historial Inquilinos
                      </Button>
                    </Box>
                    <Button
                      variant="outlined"
                      size="small"
                      fullWidth
                      color="error"
                      startIcon={<DeleteIcon />}
                      onClick={() => setRemoveTenantDialogOpen(true)}
                      sx={{
                        borderColor: '#FF9800',
                        color: '#FF9800',
                        '&:hover': {
                          borderColor: '#F57C00',
                          backgroundColor: alpha('#FF9800', 0.1)
                        }
                      }}
                    >
                      Eliminar Inquilino
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            ) : (
              <Card sx={{ position: 'sticky', top: 20 }}>
                <CardContent sx={{ p: 4, textAlign: 'center' }}>
                  <Typography variant="h6" fontWeight="bold" color="#2E7D7A" sx={{ mb: 2 }}>
                    Apartamento {statusInfo.label}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    {apartmentById?.status === ApartmentStatus.DISPONIBLE
                      ? 'Este apartamento está disponible para alquilar'
                      : 'Este apartamento está en mantenimiento'
                    }
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Button 
                    variant="contained" 
                    fullWidth
                    startIcon={<PersonIcon />}
                    onClick={() => setAddTenantDialogOpen(true)}
                    sx={{
                      backgroundColor: '#4DB6AC',
                      '&:hover': {
                        backgroundColor: '#26A69A'
                      }
                    }}
                  >
                    Asignar Inquilino
                  </Button>
                  <Button
                    variant="outlined"
                    fullWidth
                    startIcon={<PersonIcon />}
                    onClick={() => setTenantsHistoryDialogOpen(true)}
                    sx={{
                      borderColor: '#4DB6AC',
                      color: '#4DB6AC',
                      '&:hover': {
                        borderColor: '#26A69A',
                        backgroundColor: alpha('#4DB6AC', 0.1)
                      }
                    }}
                    >
                      Historial Inquilinos
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            )}
          </Grid>
        </Grid>
      </Container>

      {/* Dialog para ver imagen completa */}
      <Dialog
        open={imageDialogOpen}
        onClose={() => setImageDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogContent sx={{ p: 0 }}>
          <img
            src={selectedImage}
            alt="Vista completa"
            style={{
              width: '100%',
              height: 'auto',
              maxHeight: '80vh',
              objectFit: 'contain'
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImageDialogOpen(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      <EditApartmentDialog
        editApartmentDialogOpen={editDialogOpen}
        setEditApartmentDialogOpen={setEditDialogOpen}
        apartmentById={apartmentById}
        handleEditApartment={handleEditSave}
      />

      {/* Dialog para confirmar eliminación */}
      <DeleteApartmentDialog
        deleteDialogOpen={deleteDialogOpen}
        setDeleteDialogOpen={setDeleteDialogOpen}
        apartmentById={apartmentById}
        handleDelete={handleDelete}
      />
      <CreateRentPaymentDialog
        addPaymentDialogOpen={addPaymentDialogOpen}
        setAddPaymentDialogOpen={setAddPaymentDialogOpen}
        apartmentById={apartmentById}
        handleAddPayment={handleAddPayment}
      />
      <PaymentHistory
        paymentHistoryDialogOpen={paymentHistoryDialogOpen}
        setPaymentHistoryDialogOpen={setPaymentHistoryDialogOpen}
        apartmentById={apartmentById}
        setAddPaymentDialogOpen={setAddPaymentDialogOpen}
        setSelectedImage={setSelectedImage}
        setImageDialogOpen={setImageDialogOpen}
      />
      <TenantHistoryDialog
        tenantsHistoryDialogOpen={tenantsHistoryDialogOpen}
        setTenantsHistoryDialogOpen={setTenantsHistoryDialogOpen}
        apartmentById={apartmentById}
        setAddTenantDialogOpen={() => {}}
      />
      <DeleteCurrentTenant
        removeTenantDialogOpen={removeTenantDialogOpen}
        setRemoveTenantDialogOpen={setRemoveTenantDialogOpen}
        apartmentById={apartmentById}
        handleRemoveTenant={handleRemoveTenant}
      />
      <AddTenantDialog
        addTenantDialogOpen={addTenantDialogOpen}
        setAddTenantDialogOpen={setAddTenantDialogOpen}
        apartmentById={apartmentById}
        handleAddTenant={handleAddTenant}
        getApartmentTypeText={getApartmentTypeText}
      />
    </Box>
  );
};

export default ApartmentDetailsPage;